import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { ItemState } from "../../redux/state";
import { AppBarSaveButton } from "@ofqual/portal-components/components/SecondaryAppBar";
import {
  buildMetaData,
  getLookupTracker,
  modelToJsonPatchDocument,
} from "@ofqual/portal-core/core/utils/modelHelpers";
import { Unit } from "../../domain/Unit";
import { updatingItem } from "../../redux/actions";
import { ModuleAppBar } from "../ModuleAppBar";
import { UnitForm } from "../form/UnitForm";
import { UnitFormProps } from "../form/UnitFormProps";
import { authSelector } from "../../redux/selectors";
import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { ManageUnitRoles } from "../../domain/userRole";
import { useEffect } from "react";
import { GuidanceDrawer } from "@ofqual/portal-components/components/Drawer";
import { guidanceService } from "../../services";
import { ExperimentalFeature } from "@ofqual/portal-components/components/ExperimentalFeature";

export const ItemDetails: React.FC<{
  state: ItemState;
  handleDrawerToggle: (isOpen: boolean) => void;
  open: boolean;
}> = ({ state, handleDrawerToggle, open }) => {
  const item = state.item!;

  const dispatch = useDispatch();

  const { isInRole, experimentalFeatures } = useAuth(authSelector);

  const canEdit = isInRole(...ManageUnitRoles);

  //const disabled = state.status === "UPDATING" || !item.isEditable || !canEdit;

  const [disabled, setDisabled] = useState(true);
  useEffect(() => {
    setDisabled(state.status === "UPDATING" || !item.isEditable || !canEdit);
  }, [state, item, canEdit]);

  const { userType, token } = useSelector(authSelector);

  const {
    handleSubmit,
    control,
    formState,
    errors,
    setValue,
    getValues,
    setError,
    clearErrors,
  } = useForm<Unit>({
    defaultValues: item,
    shouldUnregister: false,
  });

  // this is important for dirtyFields to be deconstructed/accessed prior to the onSubmit process
  const { dirtyFields } = formState;
  const lookupTracker = useMemo(() => getLookupTracker(), []);

  const onSubmit = (newModel: Unit) => {
    const changedFields = Object.keys(dirtyFields) as (keyof Unit)[];

    const meta = buildMetaData(newModel, item, changedFields, lookupTracker);

    const document = modelToJsonPatchDocument(newModel, changedFields);
    dispatch(updatingItem(item.id, { document, meta }));
  };

  const unitFormProps: UnitFormProps = {
    user: { userType, token },
    disabled,
    control,
    errors,
    formState,
    setValue: (name, value) => setValue(name, value),
    getValues,
    registerCustomError: (name: keyof Unit, isError: boolean) => {
      const isCurrentlyARegisteredError = Object.keys(errors).includes(name);
      if (isError && !isCurrentlyARegisteredError) {
        setError(name, {});
      }
      if (!isError && isCurrentlyARegisteredError) {
        clearErrors(name);
      }
    },
    item,
    lookupTracker,
    isEditForm: true,
  };

  const mappedGuidanceService = useMemo(
    () => ({
      get: () =>
        guidanceService.get({ module: "units", key: "edit-unit" }, { token }),
    }),
    [token]
  );

  return (
    <>
      <div>
        <ModuleAppBar breadcrumbs={[{ label: `${item.title}` }]}>
          {canEdit && (
            <AppBarSaveButton
              onClick={handleSubmit(onSubmit)}
              isSubmitting={state.status === "UPDATING"}
              isSubmitted={state.lastCrudResult.isSuccess}
              disabled={disabled}
              dataTest="save"
            />
          )}
          <ExperimentalFeature
            experimentalModuleName="portal-units"
            experimentalFeatures={experimentalFeatures}
            experimentalFeatureVisibleName="unitsGuidancecomplete"
          >
            <GuidanceDrawer
              open={open}
              handleDrawerToggle={handleDrawerToggle}
              service={mappedGuidanceService}
            />
          </ExperimentalFeature>
        </ModuleAppBar>
        <UnitForm {...unitFormProps} />
      </div>
    </>
  );
};
